import type { ColumnType, GridColumn, GridOption, GridValue } from './types';
import { localNumber } from '../../../lib/units';

const TRUE_WORDS = new Set([
  'x',
  '✓',
  '✔',
  'yes',
  'y',
  'true',
  '1',
  'ja',
  'j',
  'on',
  '+48',
  '48v',
  '+48v',
  'p48',
  'wahr',
  '●',
]);
const FALSE_WORDS = new Set(['', 'no', 'n', 'false', '0', 'nein', 'off', '-', 'falsch']);

/** Spreadsheet-ish boolean: `x`, `yes`, `1`, `+48` … are true; empty, `no`, `0` … are false. */
export function parseBoolean(text: string): boolean | undefined {
  const t = text.trim().toLowerCase();
  if (TRUE_WORDS.has(t)) return true;
  if (FALSE_WORDS.has(t)) return false;
  return undefined;
}

/** Number with `.` or `,` as decimal separator; empty text is the empty value. */
export function parseNumber(text: string): number | '' | undefined {
  const t = text.trim();
  if (t === '') return '';
  if (!/^[-+]?\d*(?:[.,]\d+)?$/.test(t) || t === '-' || t === '+') return undefined;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

function matchOption(options: readonly GridOption[], text: string): string | undefined {
  const t = text.trim().toLowerCase();
  return options.find((o) => o.value.toLowerCase() === t || o.label.toLowerCase() === t)?.value;
}

/** Splits a multi-select cell (`a, b; c`). */
export function splitList(text: string): string[] {
  return text
    .split(/[,;|\n]/)
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

export function optionsOf<R>(col: GridColumn<R>, row: R | undefined): readonly GridOption[] {
  const o = col.options;
  if (o === undefined) return [];
  return typeof o === 'function' ? o(row) : o;
}

/** Generic text → value conversion for a column type (columns may override with `parse`). */
export function parseByType(
  type: ColumnType,
  text: string,
  options: readonly GridOption[] = [],
): GridValue | undefined {
  switch (type) {
    case 'text':
    case 'autocomplete':
      return text.trim();
    case 'number':
      return parseNumber(text);
    case 'boolean':
      return parseBoolean(text);
    case 'select':
      return text.trim() === '' ? '' : matchOption(options, text);
    case 'multiselect': {
      const parts = splitList(text);
      const values = parts
        .map((p) => matchOption(options, p))
        .filter((v): v is string => v !== undefined);
      return parts.length > 0 && values.length === 0 ? undefined : [...new Set(values)];
    }
    case 'send': {
      // a number is a level, 0 and 1 too (0 dB is unity, OIL-173); on / off are words or Space
      const n = parseNumber(text.replace(/\s*db$/i, ''));
      if (typeof n === 'number') return n;
      const b = parseBoolean(text);
      if (b !== undefined) return b;
      return n === '' ? false : n;
    }
  }
}

export function parseCell<R>(
  col: GridColumn<R>,
  text: string,
  row: R | undefined,
): GridValue | undefined {
  if (col.parse) return col.parse(text, row);
  return parseByType(col.type, text, optionsOf(col, row));
}

/** Text of a value (display + clipboard): booleans as `x` / empty, lists comma separated. */
export function formatByType(
  type: ColumnType,
  value: GridValue,
  options: readonly GridOption[] = [],
): string {
  if (Array.isArray(value)) {
    return value.map((v) => options.find((o) => o.value === v)?.label ?? v).join(', ');
  }
  if (typeof value === 'boolean') return value ? 'x' : '';
  if (type === 'select') return options.find((o) => o.value === value)?.label ?? String(value);
  // decimal comma in German (OIL-200)
  return typeof value === 'number' ? localNumber(value, 3) : String(value);
}

export function formatCell<R>(col: GridColumn<R>, row: R): string {
  const v = col.value(row);
  return col.format ? col.format(v, row) : formatByType(col.type, v, optionsOf(col, row));
}

export function isEditable<R>(col: GridColumn<R>, row: R | undefined): boolean {
  const e = col.editable ?? false;
  if (typeof e === 'boolean') return e;
  return row === undefined ? true : e(row);
}

/** Value a cell takes when cleared with Delete. */
export function clearedValue(type: ColumnType): GridValue {
  if (type === 'boolean' || type === 'send') return false;
  if (type === 'multiselect') return [];
  return '';
}

export function sameValue(a: GridValue, b: GridValue): boolean {
  if (Array.isArray(a) && Array.isArray(b))
    return a.length === b.length && a.every((v, i) => v === b[i]);
  return a === b;
}

/** Space on a boolean / send cell. */
export function toggledValue(type: ColumnType, value: GridValue): GridValue | undefined {
  if (type === 'boolean') return value !== true;
  if (type === 'send') return value === false || value === '';
  return undefined;
}
